// src/modules/calculators/NoiseCalculator.jsx
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Volume2, Plus, Trash2, FileDown, 
  ShieldAlert, CheckCircle2,  Info, Monitor 
} from "lucide-react";
import { 
  calculateNoiseExposure, 
  NOISE_REGS, 
  getNoiseActionText 
} from "../../utils/noiseFormulas";
import { generateNoiseReport } from "../../services/reports/NoiseReport";
import { mergeCalculatorsCms } from "../../lib/calculatorsCms";

const NoiseCalculator = ({ cms: cmsRaw = null }) => {
  const cms = useMemo(() => mergeCalculatorsCms(cmsRaw), [cmsRaw]);
  const [reg, setReg] = useState("niosh");
  const [segments, setSegments] = useState([
    { id: 1, loc: "", db: "", hours: "", minutes: "" }
  ]);

  const protocols = useMemo(() => {
    const out = {};
    for (const key of Object.keys(NOISE_REGS)) {
      const base = NOISE_REGS[key];
      const over = cms.ruido.protocols[key] || {};
      out[key] = { ...base, name: over.name || base.name, desc: over.desc || base.desc };
    }
    return out;
  }, [cms]);

  // Cálculo reactivo
  const results = useMemo(() => calculateNoiseExposure(segments, reg), [segments, reg]);
  const actionText = getNoiseActionText(results.twa, reg, cms.ruido.diagnostics);

  const addRow = () => {
    setSegments([...segments, { id: Date.now(), loc: "", db: "", hours: "", minutes: "" }]);
  };

  const updateRow = (id, field, val) => {
    setSegments(segments.map(s => s.id === id ? { ...s, [field]: val } : s));
  };

  const removeRow = (id) => {
    if (segments.length > 1) {
      setSegments(segments.filter(s => s.id !== id));
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-12 lg:gap-8">
      
      {/* IZQUIERDA: CONFIGURACIÓN Y ENTRADA */}
      <div className="space-y-4 lg:col-span-8 lg:space-y-6">
        
        {/* Card: Selección de Norma */}
        <div className="rounded-xl border border-border bg-surface p-4 sm:rounded-2xl sm:p-6">
          <div className="mb-3 flex items-center gap-2 sm:mb-4">
            <Monitor size={16} className="shrink-0 text-brand-primary" />
            <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 sm:text-xs">Protocolo de Evaluación</span>
          </div>
          <div className="mb-4 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] sm:mb-6 sm:flex-wrap sm:overflow-visible [&::-webkit-scrollbar]:hidden">
            {Object.keys(protocols).map((key) => (
              <button 
                key={key} 
                type="button"
                onClick={() => setReg(key)}
                className={`shrink-0 cursor-pointer rounded-xl px-3.5 py-2.5 text-[11px] font-bold transition-all sm:px-5 sm:py-3 sm:text-xs ${
                  reg === key 
                  ? 'bg-brand-primary text-white shadow-md' 
                  : 'border border-border bg-white text-gray-500 hover:border-brand-primary'
                }`}
              >
                {protocols[key].name}
              </button>
            ))}
          </div>
          <AnimatePresence mode="wait">
    <motion.div
      key={reg}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      className="p-5 bg-white rounded-xl border-l-4 border-brand-accent shadow-sm"
    >
      <p className="text-sm md:text-base text-gray-700 leading-relaxed font-medium">
        <span className="text-brand-primary font-bold">{protocols[reg].name}:</span> {protocols[reg].desc}
      </p>
      
      {/* DETALLE TÉCNICO RÁPIDO */}
      <div className="mt-3 flex gap-6">
        <div className="text-[11px] font-bold text-gray-400 uppercase">
          Tasa: <span className="text-brand-dark">{protocols[reg].exrate} dB</span>
        </div>
        <div className="text-[11px] font-bold text-gray-400 uppercase">
          Límite: <span className="text-brand-dark">{protocols[reg].crit} dB(A)</span>
        </div>
      </div>
    </motion.div>
  </AnimatePresence>
        </div>

        {/* Card: Tabla Dinámica */}
        <div className="rounded-2xl border border-border bg-white p-4 shadow-sm sm:rounded-[32px] sm:p-6">
          <div className="flex justify-between items-center mb-8">
            <h3 className="font-display font-bold text-xl text-brand-dark flex items-center gap-3">
              <Volume2 className="text-brand-primary" /> Registro por Áreas
            </h3>
            <button 
              onClick={addRow}
              className="bg-brand-soft text-brand-primary px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 hover:bg-brand-primary hover:text-white transition-all cursor-pointer"
            >
              <Plus size={14}/> AÑADIR TRAMO
            </button>
          </div>

          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {segments.map((s) => (
                <motion.div 
                  layout
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  key={s.id} 
                  className="group grid grid-cols-12 items-end gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3 sm:gap-3 sm:rounded-2xl sm:p-5"
                >
                  <div className="col-span-5">
                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">Ubicación / Proceso</label>
                    <input 
                      type="text" value={s.loc} placeholder="Ej: Sala de Máquinas"
                      onChange={e => updateRow(s.id, 'loc', e.target.value)}
                      className="w-full mt-1 p-2.5 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-brand-accent transition-all"
                    />
                  </div>
                  <div className="col-span-3">
                    <label className="text-[9px] font-black text-gray-400 uppercase ml-1">Nivel dB(A)</label>
                    <input 
                      type="number" value={s.db} placeholder="85"
                      onChange={e => updateRow(s.id, 'db', e.target.value)}
                      className="w-full mt-1 p-2.5 bg-white border border-gray-200 rounded-xl outline-none font-mono font-bold text-brand-primary"
                    />
                  </div>
                  <div className="col-span-3 flex gap-2">
                    <div className="w-1/2">
                      <label className="text-[9px] font-black text-gray-400 uppercase text-center block">Horas</label>
                      <input 
                        type="number" value={s.hours} placeholder="0"
                        onChange={e => updateRow(s.id, 'hours', e.target.value)}
                        className="w-full mt-1 p-2.5 bg-white border border-gray-200 rounded-xl text-center outline-none"
                      />
                    </div>
                    <div className="w-1/2">
                      <label className="text-[9px] font-black text-gray-400 uppercase text-center block">Min</label>
                      <input 
                        type="number" value={s.minutes} placeholder="0"
                        onChange={e => updateRow(s.id, 'minutes', e.target.value)}
                        className="w-full mt-1 p-2.5 bg-white border border-gray-200 rounded-xl text-center outline-none"
                      />
                    </div>
                  </div>
                  <div className="col-span-1 text-right">
                    <button 
                      onClick={() => removeRow(s.id)}
                      className="p-2 text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
                    >
                      <Trash2 size={18}/>
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* DERECHA: DASHBOARD DE RESULTADOS */}
      <div className="lg:col-span-4 space-y-6">
        
        {/* Card Principal: Dosis (Ahora cambia de color según el riesgo) */}
        <motion.div 
          animate={{ 
            backgroundColor: results.status === 'Peligro' ? '#991b1b' : // Rojo oscuro
                            results.status === 'Precaución' ? '#d97706' : // Ámbar
                            '#0d2a1b' // Verde AGDYSA (Original)
          }}
          className="p-8 rounded-[40px] text-white shadow-2xl relative overflow-hidden text-center transition-colors duration-500"
        >
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 opacity-20 blur-3xl rounded-full" />
          <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-white/60">Dosis de Exposición</span>
          <div className="text-7xl font-display font-black my-4 tabular-nums">
            {results.dose}<span className="text-2xl">%</span>
          </div>
          <div className="flex flex-col gap-2 items-center">
            <div className="px-4 py-1.5 rounded-full bg-white/20 text-[11px] font-bold uppercase tracking-widest">
              {results.twa} dB(A) {results.label}
            </div>
            <span className="text-[10px] text-white/60 uppercase tracking-tighter">Jornada evaluada: {results.totalTime} h</span>
          </div>
        </motion.div>

        {/* Card: Acción Requerida */}
        <div className={`p-8 rounded-[32px] border shadow-sm space-y-4 ${
          results.status === 'Peligro' ? 'bg-red-50 border-red-100' : 
          results.status === 'Precaución' ? 'bg-yellow-50 border-yellow-100' : 'bg-green-50 border-green-100'
        }`}>
          <div className="flex items-center gap-3">
            {results.status === 'Peligro' ? <ShieldAlert className="text-red-600"/> : <CheckCircle2 className="text-brand-primary"/>}
            <h4 className="font-bold text-brand-dark uppercase text-xs tracking-widest">Diagnóstico Técnico</h4>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-medium italic">
            "{actionText}"
          </p>
          
          <button 
            className="w-full mt-6 bg-brand-dark text-brand-accent py-4 rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-black transition-all active:scale-95 shadow-lg cursor-pointer"
            onClick={() => generateNoiseReport(segments, results, reg, cms)} // LLAMADA AL SERVICIO
          >
            <FileDown size={18}/> Descargar Informe PDF
          </button>
        </div>

        {/* Info adicional */}
        <div className="p-6 bg-brand-soft/30 rounded-2xl border border-brand-soft flex gap-3">
          <Info size={20} className="text-brand-primary shrink-0" />
          <p className="text-[10px] text-brand-primary leading-tight font-medium">
            {cms.ruido.footerNote}
          </p>
        </div>

      </div>
    </div>
  );
};

export default NoiseCalculator;
/*<!--© 2026 Todos los derechos reservados Desarrollado por JEMAprod-Tecnologia-->*/